import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: block;
      /* height is applied via inline style from scrollHeight property */
    }

    /* ── Outer shell ─────────────────────────────────────────────────── */
    .uwc-vs {
      display:        flex;
      flex-direction: column;
      box-sizing:     border-box;
      /* height is set via inline style from the scrollHeight property */
      border:         var(--uwc-vs-border,        1px solid ${t.border});
      border-radius:  var(--uwc-vs-radius,        ${t.radiusMd});
      background:     var(--uwc-vs-bg,            ${t.surface});
      overflow:       hidden;
    }

    /* ── Header slot ─────────────────────────────────────────────────── */
    .uwc-vs__header {
      padding:      var(--uwc-vs-header-padding, ${t.space2} ${t.space3});
      border-bottom:1px solid var(--uwc-vs-divider, ${t.borderSubtle});
      flex-shrink:  0;
    }

    /* ── Scroll viewport ─────────────────────────────────────────────── */
    .uwc-vs__viewport {
      overflow-y:      auto;
      overflow-x:      hidden;
      flex:            1;
      min-height:      0;        /* allow flex child to shrink below content size */
      position:        relative;
      /*
       * Disable browser scroll-anchoring. Without this, when Lit replaces
       * rendered items with a taller paddingBefore spacer the browser adjusts
       * scrollTop to compensate, which fires another scroll event and creates
       * a runaway scroll loop to the end of the list.
       */
      overflow-anchor: none;
      scrollbar-width: thin;
      scrollbar-color: var(--uwc-vs-scrollbar, ${t.borderControl}) transparent;
    }
    .uwc-vs__viewport::-webkit-scrollbar         { width: 6px; }
    .uwc-vs__viewport::-webkit-scrollbar-track   { background: transparent; }
    .uwc-vs__viewport::-webkit-scrollbar-thumb   {
      background:    var(--uwc-vs-scrollbar, ${t.borderControl});
      border-radius: ${t.radiusFull};
    }

    /* ── Spacers that maintain the full-list scroll height ───────────── */
    .uwc-vs__spacer {
      flex-shrink:     0;
      width:           100%;
      overflow-anchor: none;
    }

    /*
     * Prevent any child of the viewport from being chosen as a scroll-anchor.
     * Without this, hover/re-render DOM mutations cause the browser to adjust
     * scrollTop, which fires a scroll event, which triggers a re-render loop.
     */
    .uwc-vs__viewport * { overflow-anchor: none; }

    /* ── Default item rendering (used when renderItem is not supplied) ── */
    .uwc-vs__item {
      display:     flex;
      align-items: center;
      padding:     0 var(--uwc-vs-item-padding-x, ${t.space3});
      height:      var(--uwc-vs-item-height, 3rem);
      border-bottom: 1px solid var(--uwc-vs-item-divider, ${t.borderSubtle});
      font-size:   var(--uwc-vs-item-font-size, ${t.fontSizeMd});
      color:       var(--uwc-vs-item-color, ${t.text});
      cursor:      pointer;
      user-select: none;
      transition:  background ${t.durationFast};
    }
    .uwc-vs__item:last-child { border-bottom: none; }
    .uwc-vs__item:hover      { background: var(--uwc-vs-item-hover-bg, ${t.hoverBg}); }
    .uwc-vs__item[aria-selected="true"] {
      background: var(--uwc-vs-item-selected-bg, ${t.selectedBg});
      color:      var(--uwc-color-primary, ${t.primary});
      font-weight:${t.fontWeightSemibold};
    }
    .uwc-vs__item[data-focused="true"] {
      outline:        2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }

    /* ── Empty state ─────────────────────────────────────────────────── */
    .uwc-vs__empty {
      display:        flex;
      align-items:    center;
      justify-content:center;
      height:         100%;
      min-height:     6rem;
      color:          var(--uwc-vs-empty-color, ${t.textMuted});
      font-size:      ${t.fontSizeMd};
    }

    /* ── Loading skeleton ────────────────────────────────────────────── */
    .uwc-vs__skeleton-item {
      display:     flex;
      align-items: center;
      gap:         ${t.space3};
      padding:     0 var(--uwc-vs-item-padding-x, ${t.space3});
      height:      var(--uwc-vs-item-height, 3rem);
      border-bottom: 1px solid ${t.borderSubtle};
    }
    .uwc-vs__skeleton-bar {
      height:       0.875rem;
      border-radius:${t.radiusSm};
      background:   linear-gradient(
        90deg,
        ${t.borderSubtle} 25%,
        ${t.border}       50%,
        ${t.borderSubtle} 75%
      );
      background-size: 200% 100%;
      animation:   uwc-vs-shimmer 1.4s ease infinite;
    }
    @keyframes uwc-vs-shimmer {
      0%   { background-position:  200% 0; }
      100% { background-position: -200% 0; }
    }

    /* ── Footer slot ─────────────────────────────────────────────────── */
    .uwc-vs__footer {
      padding:     var(--uwc-vs-footer-padding, ${t.space2} ${t.space3});
      border-top:  1px solid var(--uwc-vs-divider, ${t.borderSubtle});
      flex-shrink: 0;
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-vs__skeleton-bar { animation: none; }
    }
  `,
];
